/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/*
 * StoryGallery.jsx
 * Created by Destin Frasier on 12/04/20
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import {
    ContentContainer,
    FlexRow,
    FlexCol,
    Heading,
    Text,
    useNonInitialEffect,
    Alert,
    URLHelper,
    announce,
} from 'sarsaparilla';

import { useDocumentTitle } from 'shared-ui/utils/seo-utils';
import StoryCategoryFilterAll from '../components/storyGallery/StoryCategoryFilterAll';
import StoryCategoryFilter2021 from '../components/storyGallery/StoryCategoryFilter2021';
import StoryCategoryFilter2022 from '../components/storyGallery/StoryCategoryFilter2022';
import StoryCategoryFilter2023 from '../components/storyGallery/StoryCategoryFilter2023';
import StoryActivityFilter from '../components/storyGallery/StoryActivityFilter';
import YearSelectFilter from '../components/storyGallery/YearSelectFilter';
import Items from '../components/storyGallery/Items';
import Winners from '../components/storyGallery/Winners';
import Finalists from '../components/storyGallery/Finalists';
import Pagination from '../components/storyGallery/Pagination';
import { fetchGalleryItems } from '../actions/galleryActions';
import HeroImage from '../components/HeroImage';

const CATEGORIES = [
    'Traditions (Old and New)',
    'Reflection Journeys',
    'RV/Campervans',
    'Family Trips',
    'Family or Group Travel',
    'Activities & Adventure',
    'Best Time Ever!',
    'Reaching for the Stars',
    'Family and Group Trips',
    'Activities and Adventure',
];
const ACTIVITIES = [
    'Boating',
    'Camping',
    'Climbing',
    'Fishing',
    'Hiking',
    'Hunting',
    'Off-Highway Vehicle',
    'Pet Friendly',
    'RV Camping',
    'Water Sports',
    'Wildlife Viewing',
];
const ITEMS_PER_PAGE = 20;

export default function StoryGallery() {
    const [searchParams] = useSearchParams();
    const searchCriteria = useMemo(
        () => ({
            q: searchParams.get('q'),
            locationID: searchParams.get('location'),
            sort: searchParams.get('sort') || 'date',
        }),
        [searchParams]
    );
    const [currentPage, setCurrentPage] = useState(
        parseInt(searchParams.get('page'), 10) || 1
    );
    const [winnerItems, setWinnerItems] = useState([]);
    const [finalistItems, setFinalistItems] = useState([]);
    const [recentItems, setRecentItems] = useState([]);
    const [categoryAggregations, setCategoryAggregations] = useState({});
    const [activityAggregations, setActivityAggregations] = useState({});
    const [categories, setCategories] = useState([]);
    const [activities, setActivities] = useState([]);
    const [total, setTotal] = useState(0);
    const [winnersLoading, setWinnersLoading] = useState(false);
    const [finalistsLoading, setFinalistsLoading] = useState(false);
    const [recentLoading, setRecentLoading] = useState(false);
    const [hasErrors, setHasErrors] = useState(false);

    const [yearSelectValue, setYearSelectValue] = useState('2023');

    const [page, setPage] = useState(1);

    const onPageChange = (newPage) => {
        setCurrentPage(newPage);
        setPage(newPage);
    };

    const updateBrowserURL = () => {
        window.history.replaceState(
            {},
            '',
            URLHelper.urlWithParams(`${window.location.pathname}`, {
                q: searchCriteria.q,
                page: currentPage === 1 ? null : currentPage,
            })
        );
    };

    const fetchWinnerItems = () => {
        if (winnerItems.length === 0) {
            setWinnersLoading(true);
        }
        if (finalistItems.length === 0) {
            setFinalistsLoading(true);
        }
        // fetch search results with applied filters
        fetchGalleryItems(
            searchCriteria.q,
            searchCriteria.locationID,
            true,
            categories.length === 0 ? [] : categories,
            activities.length === 0 ? [] : activities,
            yearSelectValue,
            'date',
            0,
            200
        )
            .then((result) => {
                if (Array.isArray(result?.data?.stories)) {
                    const winners = [];
                    const finalists = [];
                    const stories = result?.data?.stories || [];

                    stories.forEach((s) => {
                        const winnerPrize = s.prizes_objs?.filter((p) => {
                            return (
                                p.name?.indexOf('Winner') >= 0 ||
                                p.name?.indexOf('grandPrize') >= 0
                            );
                        });
                        const finalistPrize = s.prizes_objs?.filter((p) => {
                            return (
                                p.name?.indexOf('monthlyFinalist') >= 0 ||
                                p.name?.indexOf('monthlyTopTen') >= 0 ||
                                p.name?.indexOf('monthlyRunner') >= 0 ||
                                p.name?.indexOf('honorableMention') >= 0 ||
                                p.name?.indexOf('categoryRunner') >= 0
                            );
                        });
                        if (winnerPrize.length > 0) {
                            winners.push(s);
                        }
                        if (finalistPrize.length > 0) {
                            finalists.push(s);
                        }

                        if (
                            winnerPrize.length === 0 &&
                            // eslint-disable-next-line no-empty
                            finalistPrize.length === 0
                        ) {
                            /* empty */
                        }
                    });

                    setWinnerItems(winners);
                    setFinalistItems(finalists);
                } else {
                    setWinnerItems([]);
                    setFinalistItems([]);
                }
                setWinnersLoading(false);
                setFinalistsLoading(false);
            })
            .catch(() => {
                setHasErrors(true);
                setWinnersLoading(false);
                setFinalistsLoading(false);
            });
    };

    const fetchRecentItems = () => {
        if (recentItems.length === 0) {
            setRecentLoading(true);
        }

        // fetch search results with applied filters
        fetchGalleryItems(
            searchCriteria.q,
            searchCriteria.locationID,
            null,
            categories.length === 0 ? [] : categories,
            activities.length === 0 ? [] : activities,
            yearSelectValue,
            'date',
            (currentPage - 1) * ITEMS_PER_PAGE,
            ITEMS_PER_PAGE
        )
            .then((result) => {
                if (Array.isArray(result?.data?.stories)) {
                    setRecentItems(result?.data?.stories);
                } else {
                    setRecentItems([]);
                }
                announce(
                    `Retrieved ${result?.data?.stories?.length || 0} results ${
                        searchCriteria.q != null
                            ? `from search for ${searchCriteria.q}`
                            : ''
                    }. You are still in the search list combobox. The search results are listed further below.`
                );
                setTotal(result?.data?.total || 0);

                setRecentLoading(false);
            })
            .catch(() => {
                setHasErrors(true);
                setRecentLoading(false);
            });
    };

    async function fetchAggregations() {
        let agg = {};
        for (let i = 0; i < CATEGORIES.length; i += 1) {
            try {
                // eslint-disable-next-line no-await-in-loop
                const result = await fetchGalleryItems(
                    searchCriteria.q,
                    searchCriteria.locationID,
                    null,
                    [CATEGORIES[i]],
                    [],
                    yearSelectValue,
                    null,
                    0,
                    1
                );
                agg[CATEGORIES[i]] = result?.data?.total;
            } catch {
                //
            }
        }
        setCategoryAggregations(agg);

        agg = {};
        for (let i = 0; i < ACTIVITIES.length; i += 1) {
            try {
                // eslint-disable-next-line no-await-in-loop
                const result = await fetchGalleryItems(
                    searchCriteria.q,
                    searchCriteria.locationID,
                    null,
                    [],
                    [ACTIVITIES[i]],
                    yearSelectValue,
                    null,
                    0,
                    1
                );
                agg[ACTIVITIES[i]] = result?.data?.total;
            } catch {
                //
            }
        }
        setActivityAggregations(agg);
    }

    useEffect(() => {
        fetchWinnerItems();
        fetchRecentItems();
        fetchAggregations();
        setCurrentPage(1);
    }, []);

    useNonInitialEffect(() => {
        updateBrowserURL();
        fetchWinnerItems();
        fetchRecentItems();
        fetchAggregations();
    }, [searchCriteria, yearSelectValue]);

    useNonInitialEffect(() => {
        updateBrowserURL();
        fetchWinnerItems();
        fetchRecentItems();
    }, [categories, activities]);

    useNonInitialEffect(() => {
        updateBrowserURL();
        fetchRecentItems();

        setTimeout(() => {
            const nextFirstElement = document.querySelector(
                '#ugc-story-gallery-top .ugc-contest-card-link-wrap'
            );
            nextFirstElement.focus();
        }, 1500);
    }, [currentPage]);

    const onApplyCategories = (newCategories) => {
        setCurrentPage(1);
        setCategories(newCategories);
    };

    const onApplyActivities = (newActivities) => {
        setCurrentPage(1);
        setActivities(newActivities);
    };

    function displayStoryTypeDropdown() {
        switch (yearSelectValue) {
            case '2023':
                return (
                    <>
                        <StoryCategoryFilter2023
                            aggregations={categoryAggregations}
                            selectedFilters={categories}
                            onApply={onApplyCategories}
                        />

                        <StoryActivityFilter
                            aggregations={activityAggregations}
                            selectedFilters={activities}
                            onApply={onApplyActivities}
                        />
                    </>
                );
            case '2022':
                return (
                    <>
                        <StoryCategoryFilter2022
                            aggregations={categoryAggregations}
                            selectedFilters={categories}
                            onApply={onApplyCategories}
                        />

                        <StoryActivityFilter
                            aggregations={activityAggregations}
                            selectedFilters={activities}
                            onApply={onApplyActivities}
                        />
                    </>
                );
            case '2021':
                return (
                    <StoryCategoryFilter2021
                        aggregations={categoryAggregations}
                        selectedFilters={categories}
                        onApply={onApplyCategories}
                    />
                );
            default:
                return (
                    <>
                        <StoryCategoryFilterAll
                            aggregations={categoryAggregations}
                            selectedFilters={categories}
                            onApply={onApplyCategories}
                        />

                        <StoryActivityFilter
                            aggregations={activityAggregations}
                            selectedFilters={activities}
                            onApply={onApplyActivities}
                        />
                    </>
                );
        }
    }

    // Set Contest Winners Year Heading
    function displayContestWinnersHeading() {
        switch (yearSelectValue) {
            case '2023':
                return 'Share Your Story 2023 Contest Winners';
            case '2022':
                return 'Share Your Story 2022 Contest Winners';
            case '2021':
                return 'Share Your Story 2021 Contest Winners';
            default:
                return 'Share Your Story Contest Winners';
        }
    }

    // Set Contest Finalists Year Heading
    function displayContestFinalistsHeading() {
        switch (yearSelectValue) {
            case '2023':
                return 'Share Your Story 2023 Contest Finalists';
            case '2022':
                return 'Share Your Story 2022 Contest Finalists';
            case '2021':
                return 'Share Your Story 2021 Contest Finalists';
            default:
                return 'Share Your Story Contest Finalists';
        }
    }

    // Set Current / Recent Year Heading
    function displayRecentYearsHeading() {
        switch (yearSelectValue) {
            case '2023':
                return 'Explore Submissions from the 2023 Contest';
            case '2022':
                return 'Explore Submissions from the 2022 Contest';
            case '2021':
                return 'Explore Submissions from the 2021 Contest';
            default:
                return 'Explore All Submissions';
        }
    }

    useDocumentTitle(`Share Your Story Contest - Gallery - ${process.env.SITE_NAME}`);

    return (
        <main className="ugc-story-gallery-page">
            <HeroImage heroTitle="Share Your Story - Story Gallery" />

            <ContentContainer className="mb-5">
                <FlexRow>
                    <FlexCol md={10} mdOffset={1} xl={6} xlOffset={3} className="mb-2">
                        <Heading
                            headingLevel={2}
                            appearance="h3"
                            className="rec-section-heading"
                        >
                            Past Share Your Story Entries
                        </Heading>

                        <Text size="lg" className="mb-2" align="center">
                            For inspiration for your next travel adventure, read stories
                            from past Share Your Story contests.
                        </Text>
                    </FlexCol>
                </FlexRow>
            </ContentContainer>

            <div className="ugc-story-gallery-search-results-ribbon-wrap mb-5">
                <ContentContainer>
                    <FlexRow
                        alignItems="center"
                        className="ugc-story-gallery-search-results-ribbon"
                    >
                        <FlexCol className="ugc-story-gallery-search-results-copy">
                            {searchCriteria.q && !recentLoading && (
                                <>
                                    {total} results include “{searchCriteria.q}”
                                </>
                            )}
                            {!searchCriteria.q && !recentLoading && <>{total} results</>}
                        </FlexCol>

                        {/* UGC Filters */}
                        <FlexCol
                            md={10}
                            className={'ugc-story-gallery-search-results-controls'}
                        >
                            <FlexRow justifyContent="end" hasGutters={false}>
                                <YearSelectFilter
                                    setOnChange={(year) => {
                                        setYearSelectValue(year);
                                        return setCurrentPage(1);
                                    }}
                                    setValue={yearSelectValue}
                                />

                                {displayStoryTypeDropdown()}
                            </FlexRow>
                        </FlexCol>
                    </FlexRow>
                </ContentContainer>
            </div>
            {hasErrors && (
                <ContentContainer className="mb-5">
                    <Alert
                        type="error"
                        heading={'There were errors processing request:'}
                        className="mb-3"
                    >
                        <ul>
                            <li>There were errors fetching stories, please try again.</li>
                        </ul>
                    </Alert>
                </ContentContainer>
            )}

            {winnerItems.length > 0 && (
                <ContentContainer className="mb-0">
                    <Heading headingLevel={2} appearance="h4" className="mb-3">
                        {displayContestWinnersHeading()}
                    </Heading>

                    <FlexRow>
                        <FlexCol>
                            <Winners items={winnerItems} isLoading={winnersLoading} />
                        </FlexCol>
                    </FlexRow>
                </ContentContainer>
            )}
            {finalistItems.length > 0 && (
                <ContentContainer className="mb-0">
                    <Heading headingLevel={2} appearance="h4" className="mb-3">
                        {displayContestFinalistsHeading()}
                    </Heading>

                    <FlexRow>
                        <FlexCol>
                            <Finalists
                                items={finalistItems}
                                isLoading={finalistsLoading}
                            />
                        </FlexCol>
                    </FlexRow>
                </ContentContainer>
            )}
            {total > 0 && (
                <ContentContainer className="pt-5 mb-5" id="ugc-story-gallery-top">
                    <Heading headingLevel={2} appearance="h4" className="mb-3">
                        {displayRecentYearsHeading()}
                    </Heading>

                    <FlexRow>
                        <FlexCol>
                            <Items items={recentItems} isLoading={recentLoading} />
                        </FlexCol>
                    </FlexRow>

                    {total > ITEMS_PER_PAGE && (
                        <FlexRow justifyContent="center" className="mb-10">
                            <FlexCol xs="variable">
                                {!recentLoading && (
                                    <Pagination
                                        start={(page - 1) * ITEMS_PER_PAGE}
                                        total={total}
                                        pageSize={ITEMS_PER_PAGE}
                                        onPage={onPageChange}
                                    />

                                    //<SearchPagination
                                    //    start={(page - 1) * PAGINATION_SIZE}
                                    //    pageSize={PAGINATION_SIZE}
                                    //    total={size(mapInventory)}
                                    //    onPage={onPageChange}
                                    ///>
                                )}
                            </FlexCol>
                        </FlexRow>
                    )}
                </ContentContainer>
            )}
            {total === 0 && !recentLoading && (
                <ContentContainer className="mb-5">
                    <Heading headingLevel={3} appearance="h4" className="mb-3">
                        No results
                    </Heading>
                </ContentContainer>
            )}
        </main>
    );
}
